import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { actesUpload, actesPost, type ActesReference } from '@/integrations/actesApi';
import { DocTypePicker } from './ReceptionPickers';
import { fetchTicketById } from '@/integrations/hubspot';
import { petitionIdOf } from '@shared/parallelRef';
import { countryOfRef, actesEn } from '@/lib/actesCountry';

/**
 * PARALLEL — the intake form the client filled in, read once.
 *
 * It answers the question that otherwise costs an hour of typing: WHO is
 * applying, who joins them, and therefore which certificates this dossier has
 * to ask for — including, when the form names the filiation, the ancestors'
 * birth and marriage records up to the French one.
 *
 * It never writes on its own. Reading produces a proposal; a person edits it
 * and says yes. On an existing dossier it is a CROSS-CHECK: what is already
 * there is shown as already there and cannot be added twice, which is the
 * whole reason this exists rather than a second import.
 *
 * The PDF travels through storage, not through the function — Vercel refuses a
 * body over 4.5 MB and these exports pass that with attachments.
 */
/* Through the section's own door, so the call carries its country (France or Portugal). */
const post = <T,>(path: string, body: unknown): Promise<T> => actesPost<T>(path, body);

function DropPdf({ lang, busy, onFile }: { lang: Lang; busy: boolean; onFile: (f: File) => void }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`actes-drop ${busy ? 'off' : ''}`}
      onClick={() => { if (!busy) input.current?.click(); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && !busy) onFile(f); }}
    >
      <KBIcon name="documents" size={22} />
      <strong>{busy ? T('Lecture du formulaire…', 'Reading the form…') : T('Déposez le PDF Parallel', 'Drop the Parallel PDF')}</strong>
      <span>PDF — Parallel</span>
      <input ref={input} type="file" hidden accept="application/pdf"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </div>
  );
}

/* ── A whole dossier, from the form ─────────────────────────────────────── */
export function ParallelImportModal({ lang, reference, onClose, onCreated }: {
  lang: Lang; reference: ActesReference; onClose: () => void; onCreated: (id: string) => void;
}) {
  const L: 'fr' | 'en' = lang === 'fr' ? 'fr' : 'en';
  const T = (fr: string, en: string) => (L === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [service, setService] = useState('');
  const [rows, setRows] = useState<{ person: string; type: string }[]>([]);
  const [lead, setLead] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [memOpen, setMemOpen] = useState(false);
  const [hubspotId, setHubspotId] = useState('');
  const [hubspotUrl, setHubspotUrl] = useState('');
  const [parallelUrl, setParallelUrl] = useState('');
  const [driveUrl, setDriveUrl] = useState('');
  /* LES DEUX PORTES SONT ICI, ET SEULEMENT ICI.
     L'identifiant Parallel vivait dans « Nouveau dossier » et le PDF ici :
     deux écrans pour une même chose, et on finissait avec deux dossiers pour
     une famille. L'identifiant rejoint le PDF : « Depuis Parallel » gère
     Parallel, en entier. */
  const [pid, setPid] = useState('');
  const [caseId, setCaseId] = useState('');

  const matchType = (label: string): string => {
    const l = (label || '').toLowerCase();
    const hit = reference.acteTypes.find((t) => t.label_fr.toLowerCase() === l)
      ?? reference.acteTypes.find((t) => t.label_fr.toLowerCase().includes(l) || (l && l.includes(t.label_fr.toLowerCase())));
    return hit?.label_fr ?? '';
  };

  const analyse = async (file: File) => {
    setBusy(true); setErr('');
    try {
      const up = await actesUpload(file, '_parallel');
      const r = await post<{ proposal: { dossier_name: string; service: string | null; proposed_actes: { person_concerned: string; acte_type: string }[] } }>(
        '/parallel/propose', { path: up.path });
      const p = r.proposal;
      setName(p.dossier_name ?? '');
      const svc = reference.services.find((s) => p.service && s.label_fr.toLowerCase().includes(String(p.service).toLowerCase()));
      setService(svc?.label_fr ?? '');
      setRows((p.proposed_actes ?? []).map((a) => ({ person: a.person_concerned, type: matchType(a.acte_type) })));
      setStep('review');
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  /* Par IDENTIFIANT : rien à exporter, le formulaire est lu chez Parallel.
     Sans identifiant mais avec un numéro de dossier client, c'est le dossier
     qui est interrogé — il porte son propre lien Parallel. */
  const readById = async () => {
    setBusy(true); setErr('');
    try {
      // An id, or a petition link pasted whole: the id inside it.
      let id = petitionIdOf(pid) || pid.trim();
      if (!id && caseId.trim()) {
        const full = await fetchTicketById('FR', caseId.trim());
        id = petitionIdOf(full?.parallelId) || '';
        if (id) setPid(id);
        else {
          setErr(T('Ce dossier client ne porte pas de lien Parallel.', 'That case carries no Parallel link.'));
          setBusy(false); return;
        }
      }
      if (!id) { setBusy(false); return; }
      const r = await post<{ proposal: { dossier_name: string; service: string | null; proposed_actes: { person_concerned: string; acte_type: string }[] } }>(
        '/parallel/propose-id', { parallelId: id });
      const pr = r.proposal;
      setName(pr.dossier_name ?? '');
      const svc = reference.services.find((x) => pr.service && x.label_fr.toLowerCase().includes(String(pr.service).toLowerCase()));
      setService(svc?.label_fr ?? '');
      setRows((pr.proposed_actes ?? []).map((a) => ({ person: a.person_concerned, type: matchType(a.acte_type) })));
      if (caseId.trim()) setHubspotId(caseId.trim());
      setStep('review');
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const r = await post<{ dossier_id: string; actes_created: number }>('/parallel/create', {
        dossier_name: name.trim(),
        service: service || null,
        lead_lawyer_id: lead || null,
        member_ids: members,
        hubspot_id: hubspotId.trim() || null,
        hubspot_url: hubspotUrl.trim() || null,
        parallel_url: parallelUrl.trim() || null,
        drive_url: driveUrl.trim() || null,
        actes: rows.filter((x) => x.person.trim()).map((x) => ({ person_concerned: x.person, acte_type: x.type })),
      });
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onCreated(r.dossier_id);
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  return createPortal(
    <div className="scrim actes-skin" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-h">
          <div>
            <div className="eyebrow">Parallel</div>
            <h3>{T('Créer le dossier depuis Parallel', 'Create the dossier from Parallel')}</h3>
          </div>
          <button className="rb x" onClick={onClose} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">

          {step === 'upload' ? (
            <>
              <p className="muted-note">
                {T('Le formulaire dit qui demande, qui l’accompagne et donc quels actes il faut réclamer.',
                  'The form says who is applying, who joins them, and therefore which records to ask for.')}
              </p>
              <div className="actes-warn">
                <strong>{T('Un Parallel partiel donne un dossier partiel', 'A partial Parallel gives a partial dossier')}</strong>
                <p>{T('Ce qui n’est pas rempli dans le formulaire ne peut pas être deviné : la chaîne d’ascendants n’est proposée que si le PDF nomme les personnes.',
                  'What the form leaves empty cannot be guessed: the ancestry is only proposed when the PDF names the people.')}</p>
              </div>
              {/* Par identifiant d'abord : c'est ce que les gens ont sous la
                  main, et il n'y a rien à exporter. */}
              <fieldset className="actes-fieldset">
                <span className="cap">{T('Par identifiant', 'By identifier')}</span>
                <div className="actes-searchline">
                  <label className="actes-find wide">
                    <KBIcon name="search" size={13} />
                    <input value={pid} onChange={(e) => setPid(e.target.value)}
                      placeholder={T('Identifiant Parallel du client…', 'The client’s Parallel id…')} />
                    {pid && <button onClick={() => setPid('')} aria-label="clear">×</button>}
                  </label>
                  <label className="actes-find">
                    <input value={caseId} onChange={(e) => setCaseId(e.target.value)}
                      placeholder={T('ou n° de dossier client…', 'or case number…')} />
                  </label>
                  <button className="btn btn-sm" disabled={busy || !(pid.trim() || caseId.trim())}
                    onClick={() => void readById()}>
                    <KBIcon name="ai" size={13} />
                    {busy ? T('Lecture…', 'Reading…') : T('Lire le formulaire', 'Read the form')}
                  </button>
                </div>
                <p className="muted-note">
                  {pid.trim()
                    ? T('Le formulaire est lu directement chez Parallel — rien à exporter.',
                        'The form is read straight from Parallel — nothing to export.')
                    : caseId.trim()
                      ? T(`Sans identifiant, le dossier client #${caseId.trim()} sera interrogé : son lien Parallel suffit.`,
                          `With no id, case #${caseId.trim()} is asked instead — its Parallel link is enough.`)
                      : T('Donnez l’identifiant Parallel, ou le numéro du dossier client.',
                          'Give the Parallel id, or the client case number.')}
                </p>
              </fieldset>

              <div className="actes-or">{T('ou déposez le PDF', 'or drop the PDF')}</div>
              <DropPdf lang={lang} busy={busy} onFile={(f) => void analyse(f)} />
            </>
          ) : (
            <>
              <div className="fgrid">
                <label className="fl">
                  <span>{T('Nom du dossier', 'Dossier name')}</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="fl">
                  <span>{T('Service', 'Service')}</span>
                  <select value={service} onChange={(e) => setService(e.target.value)}>
                    <option value="">—</option>
                    {reference.services.map((s) => <option key={s.id} value={s.label_fr}>{s.label_fr}</option>)}
                  </select>
                </label>
                <label className="fl">
                  <span>{T('Avocat référent', 'Lead lawyer')}</span>
                  <select value={lead} onChange={(e) => setLead(e.target.value)}>
                    <option value="">—</option>
                    {reference.profiles.filter((p) => p.is_lawyer).map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
                    ))}
                  </select>
                </label>
                <div className="fl">
                  <span>{T('Collaborateurs', 'Collaborators')}</span>
                  <div className="actes-picks">
                    {reference.profiles.filter((p) => members.includes(p.id)).map((p) => (
                      <span key={p.id} className="actes-pick actes-skin">
                        {p.full_name ?? p.email}
                        <button onClick={() => setMembers((x) => x.filter((i) => i !== p.id))} aria-label="remove">×</button>
                      </span>
                    ))}
                    <div className="actes-pick-anchor">
                      <button className="actes-pick-add" onClick={() => setMemOpen((o) => !o)}>
                        <KBIcon name="plus" size={12} /> {T('Ajouter', 'Add')}
                      </button>
                      {memOpen && (
                        <>
                          <div className="mw-dd-scrim" onClick={() => setMemOpen(false)} />
                          <div className="actes-pick-menu">
                            {reference.profiles.filter((p) => !members.includes(p.id)).map((p) => (
                              <button key={p.id} onClick={() => { setMembers((x) => [...x, p.id]); setMemOpen(false); }}>
                                {p.full_name ?? p.email}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <fieldset className="actes-fieldset">
                <span className="cap">{T('Liens externes', 'External links')}</span>
                <div className="fgrid">
                  <input value={hubspotId} onChange={(e) => setHubspotId(e.target.value)} placeholder={T('N° de commande HubSpot', 'HubSpot order id')} />
                  <input value={hubspotUrl} onChange={(e) => setHubspotUrl(e.target.value)} placeholder="https://app.hubspot.com/…" />
                  <input value={parallelUrl} onChange={(e) => setParallelUrl(e.target.value)} placeholder="https://app.parallel.so/…" />
                  <input value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} placeholder="https://drive.google.com/…" />
                </div>
              </fieldset>

              <span className="cap">{T(`Actes proposés (${rows.length})`, `Proposed records (${rows.length})`)}</span>
              <div className="actes-rows">
                {rows.map((r, i) => (
                  <div key={i} className="actes-row-edit">
                    <input value={r.person} placeholder={T('Personne concernée', 'Person concerned')}
                      onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, person: e.target.value } : x)))} />
                    <DocTypePicker lang={L} types={reference.acteTypes} value={r.type}
                      onChange={(label) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, type: label } : x)))} />
                    <button className="actes-row-act" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                      <KBIcon name="trash" size={13} />
                    </button>
                  </div>
                ))}
                <button className="btn btn-sm btn-ghost"
                  onClick={() => setRows((rs) => [...rs, { person: '', type: '' }])}>
                  <KBIcon name="plus" size={13} /> {T('Ajouter une ligne', 'Add a line')}
                </button>
              </div>

              {err && <p className="muted-note ni-err">⚠ {err}</p>}
            </>
          )}
          {step === 'upload' && err && <p className="muted-note ni-err">⚠ {err}</p>}
        </div>
        {/* Le pied est toujours là, comme dans la maquette : à l'étape du
            dépôt il dit ce qui viendra (« Lire le formulaire », grisé tant
            qu'il n'y a rien à lire), à l'étape suivante il agit. */}
        <div className="modal-f">
          <span className="spacer" />
          {step === 'upload' ? (
            <>
              <button className="btn" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
              <button className="btn btn-p" disabled>{busy ? '…' : T('Lire le formulaire', 'Read the form')}</button>
            </>
          ) : (
            <>
              <button className="btn" onClick={() => setStep('upload')}>{T('Retour', 'Back')}</button>
              <button className="btn btn-p" disabled={busy || !name.trim()} onClick={() => void create()}>
                {busy ? '…' : T(`Créer le dossier et ${rows.filter((r) => r.person.trim()).length} demandes`,
                  `Create the dossier and ${rows.filter((r) => r.person.trim()).length} requests`)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Completing a dossier that already exists ───────────────────────────── */
export function ParallelComplementModal({ lang, reference, dossierId, parallelId, onClose }: {
  lang: Lang; reference: ActesReference; dossierId: string;
  /** Portugal: the case's own Parallel petition, read without a PDF. */
  parallelId?: string | null;
  onClose: () => void;
}) {
  const L: 'fr' | 'en' = lang === 'fr' ? 'fr' : 'en';
  const T = (fr: string, en: string) => (L === 'fr' ? fr : actesEn(en));
  /* PORTUGAL names each document in words (it becomes the request line). */
  const pt = countryOfRef(reference) === 'PT';
  const qc = useQueryClient();
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [items, setItems] = useState<{ person_concerned: string; acte_type: string; existing: boolean; include: boolean }[]>([]);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [addService, setAddService] = useState(true);

  const read = async (body: Record<string, unknown>) => {
    const r = await post<{ items: { person_concerned: string; acte_type: string; existing: boolean }[]; suggested_service: string | null; reason?: string }>(
      `/dossiers/${dossierId}/parallel`, body);
    if (r.reason === 'empty') throw new Error('The Parallel form has no answers yet.');
    setItems((r.items ?? []).map((i) => ({ ...i, include: !i.existing })));
    setSuggested(r.suggested_service ?? null);
    setStep('review');
  };

  const analyse = async (file: File) => {
    setBusy(true); setErr('');
    try {
      const up = await actesUpload(file, '_parallel');
      await read({ path: up.path });
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const fromParallel = async () => {
    if (!parallelId) return;
    setBusy(true); setErr('');
    try { await read({ parallelId }); } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const apply = async () => {
    setBusy(true); setErr('');
    try {
      await post(`/dossiers/${dossierId}/parallel/apply`, {
        actes: items.filter((i) => !i.existing && i.include).map((i) => ({ person_concerned: i.person_concerned, acte_type: i.acte_type })),
        service: addService ? suggested : null,
      });
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const newCount = items.filter((i) => !i.existing && i.include).length;

  return createPortal(
    <div className="scrim actes-skin" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="modal-h">
          <div>
            <div className="eyebrow">Parallel</div>
            <h3>{T('Compléter depuis Parallel', 'Complete from Parallel')}</h3>
          </div>
          <button className="rb x" onClick={onClose} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">

          {step === 'upload' ? (
            <>
              <p className="muted-note">
                {T('Le formulaire est comparé à ce que le dossier contient déjà : ce qui y est ne peut pas être ajouté deux fois.',
                  'The form is compared with what the dossier already holds: what is there cannot be added twice.')}
              </p>
              {pt && (
                /* The case carries its petition: read it straight from Parallel. */
                <div className="actes-searchline">
                  <button className="btn btn-sm" disabled={busy || !parallelId} onClick={() => void fromParallel()}>
                    <KBIcon name="ai" size={13} /> {busy ? 'Reading…' : 'Read the applicants from Parallel'}
                  </button>
                  <span className="muted-note">{parallelId
                    ? `Petition ${parallelId} — nothing to export.`
                    : 'This case carries no Parallel petition: drop the PDF instead.'}</span>
                </div>
              )}
              {pt && <div className="actes-or">or drop the PDF</div>}
              <DropPdf lang={lang} busy={busy} onFile={(f) => void analyse(f)} />
              {err && <p className="muted-note ni-err">⚠ {err}</p>}
            </>
          ) : (
            <>
              {suggested && (
                <label className="actes-check">
                  <input type="checkbox" checked={addService} onChange={(e) => setAddService(e.target.checked)} />
                  {T(`Ajouter le service « ${suggested} » au dossier`, `Add the “${suggested}” service to the dossier`)}
                </label>
              )}
              <div className="actes-rows">
                {items.map((it, i) => (
                  <div key={i} className={`actes-row-edit ${it.existing ? 'there' : ''}`}>
                    <input type="checkbox" disabled={it.existing} checked={!it.existing && it.include}
                      onChange={(e) => setItems((rs) => rs.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)))} />
                    <input disabled={it.existing} value={it.person_concerned}
                      onChange={(e) => setItems((rs) => rs.map((x, j) => (j === i ? { ...x, person_concerned: e.target.value } : x)))} />
                    {pt ? (
                      <input disabled={it.existing} value={it.acte_type} placeholder="Document requested"
                        onChange={(e) => setItems((rs) => rs.map((x, j) => (j === i ? { ...x, acte_type: e.target.value } : x)))} />
                    ) : (
                    <DocTypePicker lang={L} types={reference.acteTypes} value={it.acte_type}
                      onChange={(label) => setItems((rs) => rs.map((x, j) => (j === i ? { ...x, acte_type: label } : x)))} />
                    )}
                    {it.existing
                      ? <span className="actes-tag">{T('déjà là', 'already there')}</span>
                      : <button className="actes-row-act" onClick={() => setItems((rs) => rs.filter((_, j) => j !== i))}>
                          <KBIcon name="trash" size={13} />
                        </button>}
                  </div>
                ))}
                <button className="btn btn-sm btn-ghost"
                  onClick={() => setItems((rs) => [...rs, { person_concerned: '', acte_type: '', existing: false, include: true }])}>
                  <KBIcon name="plus" size={13} /> {T('Ajouter une ligne', 'Add a line')}
                </button>
              </div>
              {err && <p className="muted-note ni-err">⚠ {err}</p>}
            </>
          )}
        </div>
        <div className="modal-f">
          <span className="spacer" />
          {step === 'upload' ? (
            <>
              <button className="btn" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
              <button className="btn btn-p" disabled>{busy ? '…' : T('Lire le formulaire', 'Read the form')}</button>
            </>
          ) : (
            <>
              <button className="btn" onClick={() => setStep('upload')}>{T('Retour', 'Back')}</button>
              <button className="btn btn-p" disabled={busy || (newCount === 0 && !(suggested && addService))} onClick={() => void apply()}>
                {busy ? '…' : T(`Ajouter ${newCount} demandes`, `Add ${newCount} requests`)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
